import clsx from 'clsx';
import { useEffect, useId, useRef, useState } from 'react';
import styles from './ToggleButtonRow.module.css';

interface Props<TValues extends string> {
  allowClickToClearSelection?: boolean;
  initialSelectedItem?: TValues | undefined | null;
  items: Readonly<TValues[]>;
  onChange?: (selectedItem: TValues | undefined) => void;
  withDesktopColumn?: boolean;
}

const ToggleButtonRow = <TValues extends string>({
  allowClickToClearSelection,
  initialSelectedItem,
  items,
  onChange,
  withDesktopColumn,
}: Props<TValues>) => {
  const [atEnd, setAtEnd] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [selectedItem, setSelectedItem] = useState(initialSelectedItem);
  const groupId = useId();
  const scrollContainer = useRef<HTMLDivElement>(null);

  const handleScroll = (
    e: Pick<React.UIEvent<HTMLDivElement>, 'currentTarget'>,
  ) => {
    const el = e.currentTarget;
    setAtStart(el.scrollLeft <= 0);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth);
  };
  useEffect(() => {
    if (scrollContainer.current) {
      handleScroll({ currentTarget: scrollContainer.current });
    }
  }, [scrollContainer.current]);

  return (
    <div
      className={clsx(
        styles.scrollGradientContainer,
        !atStart && styles.leftGradient,
        !atEnd && styles.rightGradient,
        withDesktopColumn && styles.withDesktopColumn,
      )}
    >
      <div
        ref={scrollContainer}
        onScroll={handleScroll}
        className={styles.scrollContainer}
      >
        <div
          className={styles.container}
          role="radiogroup"
          aria-label="Options"
        >
          {items.map((item) => {
            const safeItem = item.replace(/[^a-zA-Z0-9_-]/g, '-');
            const inputId = `${groupId}-${safeItem}`;

            return (
              <div className={styles.item} key={item}>
                <input
                  className={styles.input}
                  type="radio"
                  id={inputId}
                  name={groupId}
                  value={item}
                  checked={selectedItem === item}
                  onClick={() => {
                    // Allow deselecting the radio button if already selected, as that is not normal browser behaviour
                    if (allowClickToClearSelection && selectedItem === item) {
                      setSelectedItem(undefined);
                      onChange?.(undefined);
                    }
                  }}
                  onChange={() => {
                    setSelectedItem(item);
                    onChange?.(item);
                  }}
                />
                <label className={styles.button} htmlFor={inputId}>
                  {item}
                </label>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ToggleButtonRow;
